/**
 * Money is always carried as an integer number of MINOR UNITS (bigint).
 * Never floats — the demo's `face: 48000` as a JS number silently loses
 * cents once fees, advance rates and FX are applied.
 */

export interface CurrencyMeta {
  /** ISO 4217 code */
  code: string;
  /** Display symbol / prefix */
  symbol: string;
  /** Number of minor units per major unit, as an exponent of 10 */
  exponent: number;
  /** Prefer symbol before the amount */
  prefix: boolean;
}

export const CURRENCIES: Record<string, CurrencyMeta> = {
  USD: { code: 'USD', symbol: '$', exponent: 2, prefix: true },
  EUR: { code: 'EUR', symbol: '€', exponent: 2, prefix: true },
  GBP: { code: 'GBP', symbol: '£', exponent: 2, prefix: true },
  AED: { code: 'AED', symbol: 'AED ', exponent: 2, prefix: true },
  KES: { code: 'KES', symbol: 'KSh ', exponent: 2, prefix: true },
  NGN: { code: 'NGN', symbol: '₦', exponent: 2, prefix: true },
  UGX: { code: 'UGX', symbol: 'USh ', exponent: 0, prefix: true },
};

export type CurrencyCode = keyof typeof CURRENCIES & string;

export function currency(code: string): CurrencyMeta {
  const c = CURRENCIES[code];
  if (!c) throw new Error(`Unknown currency: ${code}`);
  return c;
}

/** Minor units per major unit, e.g. 100 for USD, 1 for UGX. */
export function minorFactor(code: string): bigint {
  return 10n ** BigInt(currency(code).exponent);
}

/** "1234.56" | 1234.56 -> 123456n for USD. Rejects excess precision. */
export function toMinor(amount: string | number, code: string): bigint {
  const exp = currency(code).exponent;
  const raw = typeof amount === 'number' ? amount.toFixed(exp) : amount.trim();
  const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(raw);
  if (!m) throw new Error(`Invalid money literal: ${raw}`);
  const [, sign, whole, frac = ''] = m;
  if (frac.length > exp) {
    throw new Error(`${code} supports ${exp} decimal places, got "${raw}"`);
  }
  const padded = frac.padEnd(exp, '0');
  const value = BigInt(whole! + padded);
  return sign ? -value : value;
}

/** 123456n -> "1234.56" for USD. Exact, no float involved. */
export function toMajorString(minor: bigint, code: string): string {
  const exp = currency(code).exponent;
  if (exp === 0) return minor.toString();
  const neg = minor < 0n;
  const abs = neg ? -minor : minor;
  const f = 10n ** BigInt(exp);
  const whole = abs / f;
  const frac = (abs % f).toString().padStart(exp, '0');
  return `${neg ? '-' : ''}${whole}.${frac}`;
}

/** Locale-grouped display string, e.g. "$48,000.00". */
export function formatMoney(minor: bigint, code: string, opts: { compact?: boolean } = {}): string {
  const meta = currency(code);
  const major = Number(toMajorString(minor, code));
  if (opts.compact) {
    const abs = Math.abs(major);
    const [div, suf] =
      abs >= 1e9 ? [1e9, 'B'] : abs >= 1e6 ? [1e6, 'M'] : abs >= 1e3 ? [1e3, 'k'] : [1, ''];
    const n = major / div;
    const body = suf ? n.toFixed(Math.abs(n) >= 100 ? 0 : 1) : Math.round(n).toLocaleString('en-US');
    return `${meta.symbol}${body}${suf}`;
  }
  const body = major.toLocaleString('en-US', {
    minimumFractionDigits: meta.exponent,
    maximumFractionDigits: meta.exponent,
  });
  return `${meta.symbol}${body}`;
}

/**
 * Apply a rate (advance %, fee %) to a minor-unit amount.
 *
 * `rateBps` is basis points as an integer — 9000 bps = 90.00%. Keeping rates
 * as integers means the whole calculation stays in exact integer arithmetic.
 * Rounding is half-up on the absolute value, so it is symmetric around zero.
 */
export function applyBps(minor: bigint, rateBps: number): bigint {
  if (!Number.isInteger(rateBps)) throw new Error(`rateBps must be an integer, got ${rateBps}`);
  const neg = minor < 0n;
  const abs = neg ? -minor : minor;
  const scaled = abs * BigInt(rateBps);
  const rounded = (scaled + 5000n) / 10000n; // half-up
  return neg ? -rounded : rounded;
}

/** Percent (1.8) -> basis points (180). */
export function pctToBps(pct: number): number {
  return Math.round(pct * 100);
}

/** Basis points (180) -> percent string ("1.80%"). */
export function bpsToPct(bps: number, dp = 2): string {
  return `${(bps / 100).toFixed(dp)}%`;
}

/**
 * Split `minor` into `n` parts that sum exactly to the original —
 * the remainder is distributed one minor unit at a time. Used for
 * pro-rata allocation of a part-payment across a lender syndicate.
 */
export function allocate(minor: bigint, weights: number[]): bigint[] {
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) throw new Error('allocate() requires positive weights');
  const out: bigint[] = [];
  let assigned = 0n;
  for (let i = 0; i < weights.length; i++) {
    const share = (minor * BigInt(Math.round(weights[i]! * 1e6))) / BigInt(Math.round(total * 1e6));
    out.push(share);
    assigned += share;
  }
  // Hand the rounding dust to the largest weight so totals always reconcile.
  let dust = minor - assigned;
  const order = weights.map((w, i) => [w, i] as const).sort((a, b) => b[0] - a[0]);
  let k = 0;
  const step = dust < 0n ? -1n : 1n;
  while (dust !== 0n) {
    const idx = order[k % order.length]![1];
    out[idx] = out[idx]! + step;
    dust -= step;
    k++;
  }
  return out;
}
